import {useEffect, useMemo, useState} from 'react';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
// import {reaction} from 'mobx';
import {type IAsset} from '@netx/core-asset-next/schema';
import {
	getCategoryAutoList,
	moveCategory,
	// OrganizeAssetsAction,
} from '@netx/core-category-next/api';
import {type ICategory} from '@netx/core-category-next/schema';
import {
	getCategories,
	getCategoryData,
	getCategoryObjects,
	setCategoryObject,
	setCategoryOrder,
} from '../api';
import {CategoryOrder} from '../constants';
import {CATEGORY_ERROR_CODE} from '../errors';
import type CategoryModel from '../models/category';
import {categoriesStore} from '../store/categories';
import {shouldEnforceAlphabetical} from '../utils';
import {CATEGORY_EVENT} from '@netx/core-category-next/actions/constants';
import type {OrganizeResultParams} from '@netx/core-category-next/types';

const createPromise = <T>() => {
	let resolver: (value: T) => void;
	let rejecter: (reason?: any) => void;
	const p = new Promise<T>((resolve, reject) => {
		resolver = resolve;
		rejecter = reject;
	});
	return [p, resolver!, rejecter!] as const;
};

type ChildCategoriesQueue = Record<
	number,
	readonly [
		Promise<ICategory[]>,
		(value: ICategory[]) => void,
		(reason?: any) => void,
	]
>;
type CategoriesQueue = Record<
	string,
	readonly [
		Promise<ICategory[]>,
		(value: ICategory[]) => void,
		(reason?: any) => void,
	]
>;
type CategoryQueue = Record<
	number,
	readonly [
		Promise<ICategory>,
		(value: ICategory) => void,
		(reason?: any) => void,
	]
>;

const waitingForCategory: CategoryQueue = {};
const waitingForCategories: CategoriesQueue = {};
const waitingForCategoryChildren: ChildCategoriesQueue = {};

type SingleOrMultiple<T extends number | number[]> = T extends number
	? ICategory
	: ICategory[];

const create = async (category: Partial<Omit<ICategory, 'categoryid'>>) => {};

const search = async (query: string) => {
	const results = await getCategoryAutoList({query});
	const ids = results.map((result) => result.value).filter((id) => id !== 1);
	const categories = await fetch(ids);
	return shouldEnforceAlphabetical()
		? categories.sort((a, b) => a.name.localeCompare(b.name))
		: categories.sort((a, b) => a.orderby - b.orderby);
};

const fetch = async <T extends number | number[]>(
	ids: T,
	signal?: AbortSignal,
): Promise<SingleOrMultiple<T>> => {
	if (!Array.isArray(ids)) {
		const id = ids as number;
		const p = (waitingForCategory[id] = createPromise<ICategory>());
		const category = await getCategoryData({id});
		categoriesStore.add(category);
		delete waitingForCategory[id];
		p[1](category);
		return category as SingleOrMultiple<T>;
	}

	const key = ids.toString();
	if (waitingForCategories[key]) {
		return waitingForCategories[key][0] as Promise<SingleOrMultiple<T>>;
	}
	const p = (waitingForCategories[key] = createPromise<ICategory[]>());
	const categories = await getCategoryObjects({ids}, signal);
	categoriesStore.add(categories);
	delete waitingForCategories[key];
	p[1](categories);
	return categories as SingleOrMultiple<T>;
};

export const fetchCategory = fetch;

export const getCategory = async (id: number) => {
	const category = categoriesStore.get(id);
	if (category) return category;
	return fetch(id);
};

export const getCategoryNow = (id: number) => {
	const category = categoriesStore.get(id);
	if (category) return category;
};

export const fetchChildren = async (id: number, signal?: AbortSignal) => {
	if (waitingForCategoryChildren[id]) {
		return waitingForCategoryChildren[id][0];
	}
	const category = await fetch(id); // categoriesStore.get(id) || (await fetch(id));
	const childCategories = category.children.map((childId) =>
		categoriesStore.get(childId),
	);
	if (childCategories.some((child) => !child)) {
		const p = (waitingForCategoryChildren[id] = createPromise<ICategory[]>());
		const childCategories = await getCategories({id}, signal);
		categoriesStore.add(childCategories);
		delete waitingForCategoryChildren[id];
		p[1](childCategories);
		return childCategories;
	}
	return childCategories;
};

const remove = async (id: number) => {};

const reorder = async (
	id: number,
	order: number[],
	orderType: CategoryOrder = CategoryOrder.MANUAL,
	threaded?: boolean,
) => {
	await setCategoryOrder({ids: order, orderType, threaded});
	return await fetch(id);
};

const update = async (category: ICategory) => {
	const updatedCategory = await setCategoryObject({category});
	categoriesStore.update(updatedCategory.categoryid, updatedCategory);
	return updatedCategory;
};

export const move = async (
	category: ICategory,
	toId: number,
	toIndex?: number,
	threaded?: boolean,
) => {
	// move to new parent
	// const updatedCategory = await update({
	// 	...category,
	// 	parentid: toId,
	// });
	const updatedCategory = await moveCategory({
		movingCategoryId: category.categoryid,
		toParentCategoryId: toId,
		childIndex: toIndex ?? 0,
	});
	categoriesStore.update(updatedCategory.categoryid, updatedCategory);
	// due to the way folders are permitted need to fetch up
	// the tree until we do not get an object not found error
	const ancestors = [...category.ancestors];
	const fetchAncestorUntilSuccess = async (): Promise<void> => {
		const ancestorId = ancestors.shift();
		if (ancestorId == null) return;
		try {
			await fetch(ancestorId);
		} catch (ex: any) {
			if (ex.code === CATEGORY_ERROR_CODE.OBJECT_NOT_FOUND) {
				// assume this was a permission issue
				// the parent category only existed to facilitate
				// navigating to the category permitted
				return fetchAncestorUntilSuccess();
			}
			// otherwise, it should not be related to moving out of a
			// permitted folder, let any other code deal with it
			throw ex;
		}
	};
	await fetchAncestorUntilSuccess();

	await fetch(toId);

	return updatedCategory;
};

export const loadAncestors = async (id: number, signal?: AbortSignal) => {
	const categoryIds = [id];

	let currentCategory = categoriesStore.get(id) || (await fetch(id));
	if (currentCategory == null) {
		throw Error(
			`error loading ancestors for category ${id}: could not find category`,
		);
	}

	while (currentCategory.parentid > 1) {
		const {parentid} = currentCategory;
		currentCategory =
			categoriesStore.get(parentid) || (await fetch(parentid, signal));
		if (currentCategory == null) {
			throw Error(
				`error loading ancestors for category ${id}: could not find parent category ${parentid}`,
			);
		}
		categoryIds.push(parentid);
		const shouldFetchChildren =
			currentCategory.children.length &&
			currentCategory.children.some(
				(childId) => categoriesStore.get(childId) == null,
			);
		if (shouldFetchChildren)
			await fetchChildren(currentCategory.categoryid, signal);
	}

	return categoryIds.reverse();
};

const methods = {
	create,
	fetch,
	fetchChildren,
	loadAncestors,
	move,
	remove,
	reorder,
	search,
	update,
};

dispatcher.on(
	'category:add:success',
	async (modelOrCategory: CategoryModel | ICategory) => {
		const attributes =
			(modelOrCategory as any).constructor.__isModel__
				? (modelOrCategory as CategoryModel).attributes
				: modelOrCategory as ICategory;
		if (categoriesStore.get(attributes.categoryid)) return;

		const categoryData: ICategory = {
			...attributes,
			id: attributes.categoryid,
		};

		categoriesStore.add(categoryData);

		const id = categoryData.ancestors[0] || 1;
		const parent = categoriesStore.get(id);
		if (!parent) return;
		fetch(id);
	},
);

dispatcher.on(
	'category:edit:success',
	(modelOrCategory: CategoryModel | ICategory, isNew?: boolean) => {
		const attributes =
			(modelOrCategory as any).constructor.__isModel__
				? (modelOrCategory as CategoryModel).attributes
				: modelOrCategory as ICategory;
		const category = categoriesStore.get(attributes.categoryid);

		if (isNew || !category) return;
		const categoryData: ICategory = {
			...attributes,
			id: category.categoryid,
		};
		categoriesStore.update(categoryData.categoryid, categoryData);
		// parent changed
		if (attributes.parentid !== category.parentid) {
			fetch(category.parentid);
			fetch(attributes.parentid);
		}
		// name changed, lets try and get the right order
		else if (attributes.name !== category.name) {
			fetch(category.parentid);
		}
	},
);

dispatcher.on('category:delete:success', (model: CategoryModel) => {
	const wasFetched = categoriesStore.get(model.id);
	categoriesStore.remove(model.id);
	if (!wasFetched) return;
	const ancestorId = model
		.get('ancestors')
		.find((id) => categoriesStore.get(id));
	fetch(ancestorId || 1);
});

dispatcher.on(`asset:deleted`, (assetId: number, asset: IAsset) => {
	fetch(
		(asset?.categories || []).filter((categoryId) =>
			categoriesStore.get(categoryId),
		),
	);
});

dispatcher.on(
	CATEGORY_EVENT.ORGANIZE_ASSETS_SUCCESS,
	(params: OrganizeResultParams) => {
		const toCategory = categoriesStore.get(params.toCategoryId);
		if (toCategory) fetch(params.toCategoryId);
		if (params.fromCategoryId) {
			const fromCategory = categoriesStore.get(params.fromCategoryId);
			if (fromCategory) fetch(params.fromCategoryId);
		}
	},
);

export type UseCategoriesArguments = {
	ignoreFetch?: boolean;
};

export const useCategories = ({ignoreFetch}: UseCategoriesArguments = {}) => {
	const [data, setData] = useState<ICategory[]>([]);
	const [loading, setLoading] = useState(!ignoreFetch);

	useEffect(() => {
		// const dispose = reaction(
		// 	() => categoriesStore.data,
		// 	(data) => {
		// 		setData(Object.values(data))
		// 	},
		// );
		const dispose = categoriesStore.subscribe((_, data) => {
			setData(Object.values(data));
		});

		return () => {
			dispose();
		};
	}, []);

	const actions = useMemo(() => {
		return Object.entries(methods).reduce((accum, [key, fn]) => {
			return {
				...accum,
				[key]: async (...args: any) => {
					const result = await (fn as any).apply(fn, args);
					return result;
				},
			};
		}, {} as typeof methods);
	}, []);

	useEffect(() => {
		if (ignoreFetch) return;
		fetch(1).finally(() => setLoading(false));
	}, [ignoreFetch]);

	return {
		...actions,
		data,
		loading,
	};
};

export default useCategories;
